import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NgHeaderTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ColorInput } from '../../_components/_shared/color-input/color-input';
import { ClrFormsModule } from '@clr/angular';
import { NgTemplateOutlet, SlicePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';

const MAX_SELECTED_ALL = 500;
const MAX_SELECTED_SHOWN = 10;
@Component({
  selector: 'app-overlay-select-search',
  imports: [
    NgSelectComponent,
    NgHeaderTemplateDirective,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
    ColorInput,
    ClrFormsModule,
    FormsModule,
    MrTranslatePipe,
    SlicePipe,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-select
      id="overlay-search"
      [items]="overlays()"
      [multiple]="true"
      [isOpen]="true"
      bindLabel="name"
      [placeholder]="'Suchen und auswählen' | mrTranslate"
      [closeOnSelect]="false"
      (search)="filter($event)"
      (add)="select($event)"
      (remove)="deselect($event)"
      (clear)="clearAll()"
      [(ngModel)]="selectedOverlays">
      <ng-template ng-multi-label-tmp let-items="items" let-clear="clear">
        @for (item of items | slice: 0 : MAX_SELECTED_SHOWN; track $index) {
          @let overlay = asOverlay(item);
          <div class="ng-value d-f clr-align-items-center">
            <span class="ng-value-icon" (click)="clear(item)" aria-hidden="true">×</span>
            <span class="color-preview round" [style.background]="overlay.color"></span>
            <span class="ng-value-label">
              @if (labelTemplate()) {
                <ng-container *ngTemplateOutlet="labelTemplate(); context: { overlay }" />
              } @else {
                {{ overlay.name }}
              }
            </span>
          </div>
        }
        @if (items.length > MAX_SELECTED_SHOWN) {
          <div class="ng-value">
            <span class="badge badge-info margin-4-l margin-2-t">+{{ items.length - MAX_SELECTED_SHOWN }}</span>
          </div>
        }
      </ng-template>

      <ng-template ng-header-tmp>
        <clr-checkbox-wrapper>
          <input
            clrCheckbox
            type="checkbox"
            [indeterminate]="!isAllFilteredSelected() && isSomeFilteredSelected()"
            [(ngModel)]="isAllFilteredSelected"
            (ngModelChange)="selectAll($event)" />
          <label class="flex-1-1-auto" style="font-size: large; font-weight: large">
            {{ 'Alle' | mrTranslate }}
          </label>
        </clr-checkbox-wrapper>
      </ng-template>

      <ng-template ng-option-tmp let-overlay="item" let-item$="item$" let-index="index">
        <div class="d-f gap-5 clr-align-items-center">
          <input clrCheckbox id="overlay-{{ index }}" type="checkbox" [ngModel]="item$.selected" />
          <app-color-input
            [isRound]="true"
            [(ngModel)]="overlay.color"
            (ngModelChange)="colorChange.emit({ ...overlay })" />

          <span class="nowrap ellipsis" [attr.title]="overlay.name">
            @if (labelTemplate()) {
              <ng-container *ngTemplateOutlet="labelTemplate(); context: { overlay }" />
            } @else {
              {{ overlay.name }}
            }
          </span>
        </div>
      </ng-template>
    </ng-select>
  `,
  styles: `
    #overlay-search {
      .ng-arrow-wrapper {
        display: none;
      }
      .ng-dropdown-panel {
        position: static !important;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class OverlaySelectSearch {
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  readonly overlays = input.required<Overlay[]>();
  readonly labelTemplate = input<TemplateRef<{ overlay: Overlay }>>();
  readonly colorChange = output<Overlay>();
  readonly selectedChange = output<{ selected: boolean; index: number }>();
  protected readonly MAX_SELECTED_SHOWN = MAX_SELECTED_SHOWN;
  protected readonly isAllFilteredSelected = linkedSignal(() => {
    const filtered = this.filteredOverlays();
    if (filtered.length === 0) return false;
    const selected = this.selectedOverlays();
    return filtered.every(overlay => selected.includes(overlay));
  });

  protected readonly isSomeFilteredSelected = computed(() => {
    const filtered = this.filteredOverlays();
    if (filtered.length === 0) return false;
    const current = this.selectedOverlays();
    return filtered.some(overlay => current.includes(overlay));
  });

  protected readonly selectedOverlays = linkedSignal<Overlay[]>(() => (this.overlays(), []));
  protected readonly filteredOverlays = linkedSignal<Overlay[]>(() => [...this.overlays()]);
  private readonly savedSelected = new Set<Overlay>();
  private filterTimeout: NodeJS.Timeout;
  protected filter(event: { term: string; items: Overlay[] }) {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = setTimeout(() => this.filteredOverlays.set(event.items), 150);
  }

  protected select(overlay: Overlay) {
    this.savedSelected.add(overlay);
    this.emitWithIndex({ selected: true, overlay });
  }
  protected deselect(overlay: Overlay) {
    this.savedSelected.delete(overlay);
    this.emitWithIndex({ selected: false, overlay });
  }

  protected selectAll(toSelect: boolean) {
    if (toSelect) {
      const selectedSet = new Set(this.selectedOverlays());

      const notSelected = this.filteredOverlays().filter(overlay => !selectedSet.has(overlay));
      if (notSelected.length > MAX_SELECTED_ALL) {
        setTimeout(() => this.isAllFilteredSelected.set(false));
        this.toastr.warning(
          this.mrTranslate.transform('Maximal ') + MAX_SELECTED_ALL,
          this.mrTranslate.transform('Auswahl blockiert')
        );
        return;
      }
      notSelected.forEach(overlay => this.select(overlay));
      this.selectedOverlays.update(arr => [...arr, ...notSelected]);
    } else {
      const filteredSet = new Set(this.filteredOverlays());

      const updatedSelection = this.selectedOverlays().filter(overlay => {
        const isVisible = filteredSet.has(overlay);
        if (isVisible) {
          this.deselect(overlay);
        }
        return !isVisible;
      });
      this.selectedOverlays.set(updatedSelection);
    }
  }
  protected clearAll() {
    this.savedSelected.forEach(overlay => this.emitWithIndex({ selected: false, overlay }));
    this.savedSelected.clear();
  }

  private emitWithIndex({ selected, overlay }: { selected: boolean; overlay: Overlay }) {
    const index = this.overlays().indexOf(overlay);
    this.selectedChange.emit({ selected, index });
  }

  protected asOverlay(item: unknown): Overlay {
    if (typeof item != 'object' || !('color' in item)) throw new Error('Not Overlay');
    return item as Overlay;
  }
}

export interface Overlay {
  key: string;
  name: string;
  color: string;
}
